import { Component, createElement } from 'react';
import { createHandler } from '@mews/std';
import { ContextProps, ResolveRouteContext } from '../routeResolveContext';
import { injectProps } from './injectProps';

export interface LinkProps<P> {
    action: string;
    route: string;
    params?: P;
    children?: React.ReactNode;

    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    push: (url: string) => void;
    replace: (url: string) => void;
    goBack: () => void;
    goForward: () => void;
}

interface LinkElementBaseProps {
    href?: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface LinkFactory<LP extends LinkElementBaseProps> {
    LinkElement?: React.ComponentType<LP>;
}

export const linkFactory = <LP extends LinkElementBaseProps>({
    LinkElement = (props: React.HTMLProps<HTMLAnchorElement>) => createElement('a', props),
}: LinkFactory<LP> = {}) => {
    class Link<P extends {}> extends Component<LinkProps<P>> {
        private url?: string;

        static contextType = ResolveRouteContext;
        declare context: ContextProps;

        constructor(props: LinkProps<P>, context: ContextProps) {
            super(props, context);
            this.parseRoute(props);
        }

        UNSAFE_componentWillReceiveProps(nextProps: LinkProps<P>) {
            this.parseRoute(nextProps);
        }

        parseRoute = ({ route, params }: LinkProps<P>) => {
            this.url = this.context.resolveRoute(route, params);
        };

        handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (
                (e.button && e.button !== 0) ||
                e.metaKey ||
                e.altKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.defaultPrevented === true
            ) {
                return;
            }
            e.preventDefault();
            if (this.props.onClick) {
                this.props.onClick(e);
            }

            const { action, ...other } = this.props;
            // @ts-expect-error
            handleAction(action, [other, this.url]);
        };

        render() {
            const {
                onClick,

                // list of properties to delete from props
                goBack,
                route,
                action,
                push,
                replace,
                goForward,
                ...other
            } = this.props;

            const linkProps = {
                ...other,
                onClick: this.handleClick,
                href: this.url,
            } as LP;

            return <LinkElement {...linkProps} />;
        }
    }

    return injectProps(Link);
};

export const Link = linkFactory<React.HTMLProps<HTMLAnchorElement>>();

const handleAction = createHandler<string, <P>(actionArguments: [LinkProps<P>, string]) => void>(
    {
        replace: ([props, url]) => props.replace(url),
        back: ([props]) => props.goBack(),
        forward: ([props]) => props.goForward(),
    },
    ([props, url]) => props.push(url)
);
